import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Alert } from "@mui/material";
import {
  futureSummary,
  editResult,
  changedFields,
} from "../helpers/teamAnalysis";
import {
  editableGoalFields,
  goalFieldLabel,
  leagueGoalComparison,
  settingChanges,
} from "../helpers/teamSettingValues";
import TeamValueChanges from "./TeamValueChanges";

export default function TeamIndividualEditor({
  data,
  teamId,
  model,
  draft,
  ranking,
  en,
  busy,
  onApplyResult,
  onApplyStat,
  onPreviewStat,
  open = false,
  focusField,
  onFocusField,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            maximumFractionDigits: 3,
            minimumFractionDigits: 2,
          })
        : "—";
  const saved = futureSummary(
    model === "result"
      ? data.result_teams?.[teamId]
      : data.ranking?.find((r) => r.team_id === Number(teamId))
  );
  const current = useMemo(
    () =>
      futureSummary(
        model === "result"
          ? draft?.[teamId]
          : ranking?.find((r) => r.team_id === Number(teamId))
      ),
    [model, draft, ranking, teamId]
  );
  const [field, setField] = useState("for_home"),
    [input, setInput] = useState(""),
    [touched, setTouched] = useState(false),
    [effect, setEffect] = useState(null),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false);
  const generation = useRef(0),
    details = useRef(null);
  useEffect(() => {
    if (focusField) {
      setField(focusField);
      if (open && details.current) details.current.open = true;
    }
  }, [focusField, open]);
  useEffect(() => {
    generation.current++;
    setEffect(null);
    setInput(
      Number.isFinite(current?.[field])
        ? String(Number(current[field].toFixed(6)))
        : ""
    );
    setTouched(false);
    setError("");
  }, [current, field]);
  useEffect(
    () => () => {
      generation.current++;
    },
    []
  );
  const change = (v) => {
    generation.current++;
    setInput(v);
    setTouched(true);
    setEffect(null);
  };
  const p = {
    team_id: Number(teamId),
    group: "summary",
    field,
    value: touched ? Number(input) : current?.[field],
  };
  const inspect = async () => {
    const g = generation.current;
    setPending(true);
    setError("");
    try {
      const result =
        model === "result"
          ? { after: editResult(current, field, p.value) }
          : await onPreviewStat(p);
      if (g === generation.current) setEffect(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setPending(false);
    }
  };
  const rows = effect
    ? model === "result"
      ? changedFields(data.result_teams?.[teamId], effect.after)
          .filter((c) => !["rank", "team_id", "total_goals"].includes(c.field))
          .map((c) => ({ id: teamId, model, group: "", ...c }))
      : settingChanges(data, {}, effect.state)
    : [];
  const rule =
    field === "draw_factor"
      ? t(
          "Niedrigerer Remis-Faktor bedeutet mehr Remisen, höherer weniger. Torwerte bleiben erhalten; keine automatische Kalibrierung.",
          "A lower draw factor means more draws, a higher factor fewer. Goal values remain unchanged; no automatic calibration."
        )
      : field.endsWith("homefactor")
      ? t(
          "Der Gesamtwert dieser Seite bleibt konstant; Heim und Auswärts werden entsprechend dem Faktor neu verteilt. Gesamtquotient und Torniveau bleiben erhalten.",
          "The overall value on this side stays constant; home and away are redistributed by the factor. Overall ratio and goal level are preserved."
        )
      : field === "total_strength"
      ? t(
          "Quotient ändern erhält das Torniveau und die Heimfaktoren.",
          "Changing the ratio preserves goal level and home factors."
        )
      : field === "total_goals"
      ? t(
          "Torniveau ändern erhält den Quotienten und die Heimfaktoren.",
          "Changing goal level preserves the ratio and home factors."
        )
      : field.endsWith("total")
      ? t(
          "Heim und Auswärts derselben Seite werden proportional verändert; die andere Seite bleibt erhalten.",
          "Home and away on this side change proportionally; the other side is preserved."
        )
      : t(
          "Nur dieser Torwert wird verändert; die drei anderen bleiben erhalten. Quotient, Summe und Heimfaktor folgen daraus.",
          "Only this goal value changes; the other three remain unchanged. Ratio, sum and home factor follow from it."
        );
  return (
    <details ref={details} className="tw-individual" open={open || undefined}>
      <summary>
        {t(
          "Einzelwerte verstehen und bearbeiten",
          "Understand and edit individual values"
        )}
      </summary>
      <p>
        {t(
          "Gespeicherte Modellwerte und Entwurf. Gesamtwerte mitteln Heim und Auswärts gleich. Quellgewichte beeinflussen nur den Vorschlag, nicht die Definition des Quotienten.",
          "Saved model values and draft. Overall values average home and away equally. Source weights affect the proposal, not the definition of the ratio."
        )}
      </p>
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Einstellung", "Setting")}</th>
              <th>{t("Gespeichert", "Saved")}</th>
              <th>{t("Entwurf", "Draft")}</th>
              <th>{t("Liga · Modell", "League · model")}</th>
              <th>Δ / %</th>
              <th>{t("Liga · real", "League · actual")}</th>
              <th>{t("Einordnung / Bearbeiten", "Interpretation / edit")}</th>
            </tr>
          </thead>
          <tbody>
            {[
              ...editableGoalFields,
              "for_homefactor",
              "against_homefactor",
              ...(model === "result" ? ["draw_factor"] : []),
            ].map((k) => {
              const c = leagueGoalComparison(data, model, k, current?.[k]);
              return (
                <tr key={k} className={field === k ? "tw-highlight" : ""}>
                  <th>{goalFieldLabel(k, en)}</th>
                  <td>{fmt(saved?.[k])}</td>
                  <td>{fmt(current?.[k])}</td>
                  <td>{fmt(c.stored)}</td>
                  <td>
                    {c.diff > 0 ? "+" : ""}
                    {fmt(c.diff)} / {c.percent > 0 ? "+" : ""}
                    {fmt(c.percent)} %
                  </td>
                  <td>{fmt(c.actual)}</td>
                  <td>
                    {k.endsWith("homefactor") && c.diff !== null
                      ? c.diff > 0
                        ? t("Höherer Heimfaktor", "Higher home factor")
                        : c.diff < 0
                        ? t("Niedrigerer Heimfaktor", "Lower home factor")
                        : t("Ligamittel", "League mean")
                      : k.startsWith("against") && c.diff !== null
                      ? c.diff < 0
                        ? t("Weniger Gegentore", "Fewer goals conceded")
                        : c.diff > 0
                        ? t("Mehr Gegentore", "More goals conceded")
                        : t("Ligamittel", "League mean")
                      : k.startsWith("for") && c.diff !== null
                      ? c.diff > 0
                        ? t("Mehr erwartete Tore", "More expected goals")
                        : c.diff < 0
                        ? t("Weniger erwartete Tore", "Fewer expected goals")
                        : t("Ligamittel", "League mean")
                      : ""}
                    <Button
                      aria-label={
                        t("Bearbeiten", "Edit") + " · " + goalFieldLabel(k, en)
                      }
                      onClick={() => {
                        setField(k);
                        onFocusField?.(k);
                        setInput(
                          Number.isFinite(current?.[k])
                            ? String(Number(current[k].toFixed(6)))
                            : ""
                        );
                        setTouched(false);
                        setEffect(null);
                        generation.current++;
                      }}
                    >
                      {t("Bearbeiten", "Edit")}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p>
        {model === "result"
          ? t(
              "Liga-Modell: Heim = gespeicherter Liga-Toreschnitt × Heimfaktor / 2; Auswärts = Toreschnitt × (2 − Heimfaktor) / 2. Defensive Heim vergleicht mit Auswärtstoren, Defensive Auswärts mit Heimtoren.",
              "League model: home = stored league goal average × home factor / 2; away = goal average × (2 − home factor) / 2. Home defence compares with away goals, away defence with home goals."
            )
          : t(
              "Liga-Modell: Durchschnitt der gespeicherten Torwerte aller Teams dieses Statistikmodells; keine reale Ergebnisbilanz.",
              "League model: average of saved goal values across this statistics model’s teams; not actual results."
            )}
        {model === "result" && (
          <>
            {" "}
            {fmt(data.result_parameters?.stored_goal_average)} ×{" "}
            {fmt(data.result_parameters?.exp_home_factor)} / 2.
          </>
        )}{" "}
        {t(
          "Liga real: tatsächlich beobachtete Tore pro Ligaspiel.",
          "League actual: observed goals per league match."
        )}
      </p>
      <label>
        {goalFieldLabel(field, en)} ·{" "}
        {t("Neuer Wert · noch nicht übernommen", "New value · not applied")}{" "}
        <input
          type="number"
          aria-label={t("Einzelwert bearbeiten", "Edit individual value")}
          value={input}
          onChange={(e) => change(e.target.value)}
          step=".001"
          disabled={busy || pending}
        />
      </label>
      <p>
        {rule}{" "}
        {model === "statistics" &&
          t(
            "Die vorhandene Statistikroutine passt die darunterliegenden Einzelstatistiken proportional an; gültige Bereichsgrenzen bleiben erhalten.",
            "The existing statistics routine adjusts underlying statistics proportionally, respecting valid ranges."
          )}
      </p>
      <Button
        disabled={busy || pending || input === "" || !(Number(input) > 0)}
        onClick={inspect}
      >
        {t("Einzelwert prüfen", "Inspect individual value")}
      </Button>
      <Button
        disabled={busy || pending || !effect}
        onClick={async () => {
          try {
            const ok =
              model === "result"
                ? onApplyResult(teamId, field, p.value)
                : await onApplyStat(p);
            if (ok !== false) setEffect(null);
          } catch (e) {
            setError(e.message);
          }
        }}
      >
        {t(
          "Einzelwert als Entwurf übernehmen",
          "Apply individual value as draft"
        )}
      </Button>
      {effect && (
        <>
          <h4>
            {t(
              "Konkrete Änderungen · noch nicht übernommen",
              "Concrete changes · not applied"
            )}
          </h4>
          <TeamValueChanges
            names={(id) => data.result_teams?.[id]?.team_name || id}
            registry={data.state?.registry}
            rows={rows}
            en={en}
          />
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </details>
  );
}
